import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, Plus, RotateCcw, Search, Trash2, Truck } from 'lucide-react';
import { operationsApi, type AssetRegistry, type AssetType } from '../api';
import { AssetCreateModal } from '../components/AssetCreateModal';
import { PageHeader, StatusPill } from '@/lib/ui';

const STATUSES = ['ACTIVE', 'IDLE', 'ALERT', 'OFFLINE', 'STALE', 'MAINTENANCE'] as const;

export function AssetListPage() {
  const [items, setItems] = useState<AssetRegistry[]>([]);
  const [total, setTotal] = useState(0);
  const [types, setTypes] = useState<AssetType[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useMemo(
    () => async () => {
      setLoading(true);
      setError(null);
      try {
        const [list, typeList] = await Promise.all([
          operationsApi.listAssets({
            search: search || undefined,
            asset_type_slug: typeFilter || undefined,
            status: statusFilter || undefined,
            include_deleted: includeDeleted,
            limit: 200,
          }),
          types.length === 0 ? operationsApi.listAssetTypes() : Promise.resolve(types),
        ]);
        setItems(list.items);
        setTotal(list.total);
        if (types.length === 0) setTypes(typeList as AssetType[]);
      } catch (e) {
        setError((e as Error).message || 'Failed to load assets');
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [search, typeFilter, statusFilter, includeDeleted],
  );

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const onExport = async () => {
    const blob = await operationsApi.exportAssetsCsv();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'assets.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const onDelete = async (id: string) => {
    if (!confirm('Soft-delete this asset?')) return;
    await operationsApi.deleteAsset(id);
    await refresh();
  };

  const onRestore = async (id: string) => {
    await operationsApi.restoreAsset(id);
    await refresh();
  };

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border px-6 py-5">
        <PageHeader
          title="Assets"
          icon={Truck}
          subtitle={loading ? 'Loading…' : `${total} ${total === 1 ? 'asset' : 'assets'}`}
          actions={
            <>
              <button className="btn-secondary" onClick={onExport}>
                <Download className="h-4 w-4" />
                Export CSV
              </button>
              <button className="btn-primary" onClick={() => setShowCreate(true)}>
                <Plus className="h-4 w-4" />
                Add Asset
              </button>
            </>
          }
          className="!mb-0"
        />
      </div>

      <div className="flex items-center gap-3 border-b border-border bg-muted/20 px-6 py-3">
        <div className="relative max-w-xs flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            className="input-search"
            placeholder="Search by code or name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="input max-w-[12rem]"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <option value="">All types</option>
          {types.map((t) => (
            <option key={t.id} value={t.slug}>
              {t.name}
            </option>
          ))}
        </select>
        <select
          className="input max-w-[10rem]"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={includeDeleted}
            onChange={(e) => setIncludeDeleted(e.target.checked)}
          />
          Show deleted
        </label>
      </div>

      {error && (
        <div className="border-b border-danger/40 bg-danger/10 px-6 py-2 text-sm text-danger">
          {error}
        </div>
      )}

      <div className="flex-1 overflow-auto px-6 py-4">
        <div className="data-table-wrapper">
          <div className="data-table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Plate</th>
                  <th>Status</th>
                  <th className="num">Odometer</th>
                  <th>Last fix</th>
                  <th className="num"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((a) => (
                  <tr key={a.id} className={a.deleted_at ? 'opacity-50' : ''}>
                    <td className="font-mono text-xs">{a.code}</td>
                    <td>
                      <Link to={`/assets/${a.id}`} className="link">
                        {a.name}
                      </Link>
                    </td>
                    <td className="muted">{a.asset_type_slug}</td>
                    <td className="font-mono text-xs">{a.plate_number ?? '—'}</td>
                    <td>
                      <StatusPill status={a.status} />
                    </td>
                    <td className="num">
                      {a.odometer_current != null
                        ? `${a.odometer_current.toLocaleString()} km`
                        : '—'}
                    </td>
                    <td className="muted text-xs">
                      {a.last_fix_at ? new Date(a.last_fix_at).toLocaleString() : 'never'}
                    </td>
                    <td className="actions text-xs">
                      {a.deleted_at ? (
                        <button className="link" onClick={() => onRestore(a.id)}>
                          <RotateCcw className="h-3.5 w-3.5" />
                          Restore
                        </button>
                      ) : (
                        <button
                          className="link text-danger hover:text-danger/80"
                          onClick={() => onDelete(a.id)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {!loading && items.length === 0 && (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-10 text-center text-sm text-muted-foreground"
                    >
                      No assets match the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showCreate && (
        <AssetCreateModal
          assetTypes={types}
          onClose={() => setShowCreate(false)}
          onCreated={async () => {
            setShowCreate(false);
            await refresh();
          }}
        />
      )}
    </div>
  );
}
